import { useState, useMemo, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import styles from './Leave.module.scss';
import { Dropdown } from '@/components/Dropdown';
import {
  leaveTypeOptions,
  paidLeaveTypeList,
  paidLeaveTypeOptions,
} from './Leave.constants';
import { Input } from '@/components/Input';
import { borderedSelectStyles } from '@/components/Dropdown/Dropdown.constants';
import { Button, buttonVariants } from '@/components/Button';
import { leaveBalanceService } from '@services/leaveBalance';
import { useSelector } from 'react-redux';
import { addNotification, notificationStates } from '@components/Notification';
import firebase from '@config/firebase.config';
import {
  validateLeaveForm,
  canUseLeaveDateSelection,
  isLeaveDateSelectableInCalendar,
} from './Leave.helper';

const database = firebase.firestore();

export const LeaveFormModal = ({
  companyManagers,
  onClose,
  fetchLeaveRequestsList,
  fetchLeaveBalance,
  leaveData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { profile } = useSelector((state) => state.auth);
  const teams = useSelector((state) => ({
    projects: state.team.projects,
    active: state.team.active,
    teams: state?.team?.teams,
  }));
  const [formData, setFormData] = useState({
    leaveType: null,
    leaveComment: null,
    leaveManager: null,
    leaveDate: null,
    paidLeaveType: null,
  });
  useEffect(() => {
    let defaultLeaveOption = leaveTypeOptions[1];
    if (leaveData?.availableLeaves > 0) {
      defaultLeaveOption = leaveTypeOptions[0];
    }
    setFormData((prev) => ({ ...prev, leaveType: defaultLeaveOption }));
  }, [leaveData]);

  const companyManagersDropdownOptions = useMemo(() => {
    return companyManagers.map((manager) => ({
      ...manager,
      label: manager.userName,
      value: manager.userUID,
    }));
  }, [companyManagers]);

  const selectedLeaveDate = useMemo(() => {
    const ld = formData.leaveDate;
    if (!ld || ld.year == null || ld.month == null || ld.day == null) {
      return null;
    }
    const y = Number(ld.year);
    const m = Number(ld.month);
    const d = Number(ld.day);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
      return null;
    }
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [formData.leaveDate]);

  const todayMin = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const validatePaidLeaveBalance = async () => {
    setIsSubmitting(true);
    if (paidLeaveTypeList.includes(formData.paidLeaveType?.value)) {
      try {
        // First check if leave policy exists
        const policy = await leaveBalanceService.getLeavePolicy(
          activeCompany?.companyId
        );

        if (!policy) {
          addNotification({
            message:
              'No paid leave policy configured. Please contact your administrator.',
            level: notificationStates.error,
          });
          return false;
        }

        // Get user's effective remaining balance (including pending requests)
        const balance =
          await leaveBalanceService.getEffectiveRemainingPaidLeave(
            profile.id,
            activeCompany?.companyId
          );

        if (!balance.hasPolicy) {
          addNotification({
            message:
              'No paid leave policy configured. Please contact your administrator.',
            level: notificationStates.error,
          });
          return false;
        }

        if (balance.effectiveRemaining <= 0) {
          addNotification({
            message:
              'You have consumed all your available paid leave days. No leave balance remaining.',
            level: notificationStates.error,
          });
          return false;
        }

        // Show informative message about pending requests
        if (balance.pendingRequests > 0) {
          addNotification({
            message: `You have ${balance.effectiveRemaining} paid leave days available for new requests (${balance.totalAllocated} total allocated - ${balance.pendingRequests} pending requests).`,
            level: notificationStates.info,
          });
        }

        return true;
      } catch (error) {
        console.error('Error checking paid leave balance:', error);
        addNotification({
          message: 'Error checking paid leave balance. Please try again.',
          level: notificationStates.error,
        });
        return false;
      } finally {
        setIsSubmitting(false);
      }
    }
    return true;
  };
  const initializeUserLeaveUsageIfNeeded = async () => {
    if (paidLeaveTypeList.includes(formData.leaveType?.value)) {
      try {
        await leaveBalanceService.initializeUserLeaveUsage(
          profile.id,
          activeCompany?.companyId
        );
      } catch (error) {
        console.error('Error initializing user leave usage:', error);
        // Don't block the application if this fails
      }
    }
  };
  const handleSubmit = async () => {
    if (!validateLeaveForm(formData)) {
      setIsSubmitting(false);
      return;
    }
    // Check paid leave balance before submission
    if (!(await validatePaidLeaveBalance())) {
      setIsSubmitting(false);
      return;
    }
    // Initialize user's leave usage document if needed
    await initializeUserLeaveUsageIfNeeded();

    // let date = new Date(parseInt(year), parseInt(month), parseInt(day));

    // let D = date.getDate();
    // let M = date.getMonth();
    // let Y = date.getFullYear()

    let leaveDate = `${formData.leaveDate.year}-${formData.leaveDate.month}-${formData.leaveDate.day}`;

    const companyLeaveRef = database
      .collection('companyDatabase')
      .doc(activeCompany?.companyId)
      .collection('leaveRequests');
    const query = companyLeaveRef
      .where('userId', '==', profile.id)
      .where('leaveDate', '==', leaveDate)
      .where('status', '!=', 'Declined');
    query.get().then((querySnap) => {
      if (!querySnap.empty) {
        addNotification({
          message: `You already have submitted a request for leave on the date - ${leaveDate}`,
          level: notificationStates.error,
        });
        setIsSubmitting(false);
        return;
      }
      const leaveType =
        formData.leaveType.value === 'Paid Leave'
          ? formData.paidLeaveType?.value
          : formData.leaveType?.value;
      const managerId =
        formData.leaveManager !== ''
          ? formData.leaveManager.userUID
          : companyManagers[0]?.userUID;
      const finalData = {
        userId: profile.id,
        companyId: activeCompany.companyId,
        companyID: activeCompany.companyId,
        teamId: teams.active?.activeTeamid || '',
        assignee: managerId,
        managerId,
        status: 'Pending',
        type: leaveType || '',
        comment: formData.leaveComment || 'No Comment',
        leaveDate,
        requestDate: new Date(),
      };
      return companyLeaveRef
        .add(finalData)
        .then(() => {
          addNotification({
            message: 'Request submitted successfully',
            level: notificationStates.success,
          });
          setFormData({
            leaveType: null,
            leaveComment: null,
            leaveManager: null,
            leaveDate: null,
          });
          fetchLeaveBalance();
          setIsSubmitting(false);
          onClose();
          fetchLeaveRequestsList();
        })
        .catch((error) => {
          addNotification({
            message: 'Request submission failed',
            level: notificationStates.error,
          });
          console.log(error);
          setIsSubmitting(false);
        });
    });
  };
  const filteredLeaveTypeOptions = useMemo(() => {
    let filteredOptions = [];
    if (leaveData?.availableLeaves === 0) {
      filteredOptions = leaveTypeOptions.filter(
        (option) => option.value !== leaveTypeOptions[0].label
      );
    } else {
      filteredOptions = leaveTypeOptions.filter(
        (option) => option.value !== leaveTypeOptions[1].label
      );
    }
    return filteredOptions;
  }, [leaveData]);
  return (
    <div className={styles.leaveFormModal}>
      <Text
        variant={textVariantsEnum.Text_md}
        fontWeight={fontWeightEnum.Medium}
      >
        Leave Form
      </Text>
      <div className={styles.leaveFormModalContent}>
        <Dropdown
          options={filteredLeaveTypeOptions}
          label="Leave Type *"
          value={formData.leaveType}
          selectStyles={borderedSelectStyles}
          onChange={(option) =>
            setFormData((prev) => ({ ...prev, leaveType: option }))
          }
        />
        {formData.leaveType?.value === 'Paid Leave' && (
          <>
            <Dropdown
              options={paidLeaveTypeOptions}
              label="Paid Leave Type*"
              value={formData.paidLeaveType}
              selectStyles={borderedSelectStyles}
              onChange={(option) =>
                setFormData((prev) => ({ ...prev, paidLeaveType: option }))
              }
            />
          </>
        )}
        <Input
          type="text"
          label="Comment"
          placeholder="Add a comment"
          value={formData.leaveComment}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, leaveComment: e.target.value }))
          }
        />
        <Dropdown
          label="Manager *"
          options={companyManagersDropdownOptions}
          value={formData.leaveManager}
          selectStyles={borderedSelectStyles}
          onChange={(option) =>
            setFormData((prev) => ({ ...prev, leaveManager: option }))
          }
        />
        <div className={styles.datePicker}>
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Normal}
            className={styles.label}
          >
            Date *
          </Text>
          <DatePicker
            className={styles.datePickerInput}
            selected={selectedLeaveDate}
            onChange={(date) => {
              if (!date) {
                setFormData((prev) => ({ ...prev, leaveDate: null }));
                return;
              }
              if (!canUseLeaveDateSelection(date)) return;
              setFormData((prev) => ({
                ...prev,
                leaveDate: {
                  year: date.getFullYear(),
                  month: date.getMonth() + 1,
                  day: date.getDate(),
                },
              }));
            }}
            placeholderText="Select date"
            dateFormat="yyyy-MM-dd"
            minDate={todayMin}
            filterDate={isLeaveDateSelectableInCalendar}
            popperClassName={styles.leaveDatePickerPopper}
          />
        </div>
        <Button
          variant={buttonVariants.primary}
          text="Submit"
          onClick={() => {
            setIsSubmitting(true);
            handleSubmit();
          }}
          loading={isSubmitting}
        />
      </div>
    </div>
  );
};
